import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { authFetch, API } from '../api';
import { useDeskSignIn, isBadgeRequired } from '../returns/DeskSignIn';
import { useScanWedge } from '../scanWedge';
import { scanSound } from '../scanSound';
import { post, detailOf } from './inboundApi';

// Moving a product to a new shelf while replenishing an inbound (docs/INBOUND.md, Fernando 30.10): the old shelf is
// full of a few leftovers of the same product, so the person takes them along instead of moving four other products.
// Three steps: (1) collect the old pieces — sizes and expected pieces at the old shelf (the WMS stock as of the last
// sync); fewer found → a manager is called and the move waits; (2) the new shelf — typed or its label scanned;
// (3) make space — what already lives there goes elsewhere, a shelf per size and the pieces left verified. Every shelf
// write is a product edit under the person, pushed to WGR. -- Claude, 2026-10-30
// 1.11: a step of a cart tablet's trip — the walker moves the product from the tablet (MoveView inside TabletTrip).

type Expected = { wms_id: number; article_number: string; size: string; expected: number; live?: number; unpicked?: number; in_flight?: number; source?: string };
type Other = { key: string; name: string; master_sku: string; sizes: { wms_id: number; article_number: string; size: string; stock: number }[] };
type Move = {
  id: number; inbound_id: number; box_id: number | null; style_key: string; from_display: string | null; to_display: string | null;
  status: 'collect' | 'needs_manager' | 'target' | 'make_space' | 'applied' | 'cancelled';
  expected: Expected[] | null; found: Record<string, number> | null; at_target: Other[] | null; count_differs: boolean; started_by: string | null;
};

const input = 'bg-[#e5e7eb] border border-[#9aa0a6] text-[#111111] rounded px-2 py-1 text-sm';

export default function InboundMove() {
  const { id = '', mid = '' } = useParams<{ id: string; mid: string }>();
  const navigate = useNavigate();
  return <MoveView id={id} mid={mid} onDone={() => navigate(`/inbounds/${id}`)} />;
}

/** The move itself — a page of its own (a manager looking), or inside a cart tablet's trip (1.11: the walker moves it). */
export function MoveView({ id, mid, onDone }: { id: string; mid: string; onDone: () => void }) {
  const role = localStorage.getItem('wms_role') || 'staff';
  const manager = role === 'admin' || role === 'manager';
  const [m, setM] = useState<Move | null>(null);
  const [boxLabel, setBoxLabel] = useState<string>('');
  const [found, setFound] = useState<Record<string, string>>({});
  const [target, setTarget] = useState('');
  const [away, setAway] = useState<Record<string, { to: string; remaining: string }>>({});
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);
  const sign = useDeskSignIn('replenish', `${API}/inbound/who`, { eager: false });   // look freely; the badge when work starts
  const canWork = !sign.who?.station || !!sign.who?.can_work;   // a P&P station looks, an inbound station works (31.10)

  const load = useCallback(async () => {
    const r = await authFetch(`${API}/inbound/${id}/move/${mid}`);
    const j = await r.json().catch(() => ({} as any));
    if (!r.ok) { setErr(j.detail || `HTTP ${r.status}`); return; }
    setM(j.move); setBoxLabel(j.box_label || '');
    if (j.move?.expected && Object.keys(found).length === 0) {
      const f: Record<string, string> = {};
      for (const e of j.move.expected) f[String(e.wms_id)] = j.move.found?.[String(e.wms_id)] != null ? String(j.move.found[String(e.wms_id)]) : String(e.expected);
      setFound(f);
    }
  }, [id, mid]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const act = async (path: string, body: any = {}) => {
    setBusy(true); setErr('');
    try {
      const r = await post(path, body);
      if (await isBadgeRequired(r)) { sign.askAgain(() => act(path, body)); return null; }
      const j = await r.json().catch(() => ({} as any));
      if (!r.ok) { setErr(typeof j.detail === 'string' ? j.detail : j.detail?.message || `HTTP ${r.status}`); scanSound('error'); return null; }
      if (j.move) setM(j.move);
      return j;
    } finally { setBusy(false); }
  };

  // a shelf label scanned fills the shelf field of the current step
  useScanWedge(code => { if (m?.status === 'target') { setTarget(code); chooseTarget(code); } }, { enabled: m?.status === 'target', skipFields: true });

  const collect = async (all: boolean) => {
    const f: Record<string, number> = {};
    for (const [k, v] of Object.entries(found)) f[k] = parseInt(v || '0', 10) || 0;
    const j = await act(`/${id}/move/${mid}/collect`, { found: f, all_found: all });
    if (j) scanSound(j.move.status === 'needs_manager' ? 'error' : 'ok');
  };
  const chooseTarget = async (code: string) => {
    const j = await act(`/${id}/move/${mid}/target`, { code });
    if (j) { scanSound('ok'); if (j.move.status === 'applied') done(); }
  };
  const makeSpace = async (skip: boolean) => {
    const half = Object.values(away).filter(v => v.remaining !== '' && !v.to.trim());
    if (!skip && half.length) { setErr('A count without a shelf: give each size you move away its new shelf, or clear the count'); return; }
    const items = skip ? [] : Object.entries(away).filter(([, v]) => v.to.trim()).map(([wms_id, v]) => ({ wms_id: Number(wms_id), to_code: v.to, remaining: v.remaining === '' ? null : Number(v.remaining) }));
    const j = await act(`/${id}/move/${mid}/make-space`, { items });
    if (j) { scanSound('ok'); done(); }
  };
  const done = () => onDone();
  const cancel = async () => { if (window.confirm('Cancel this move? Nothing has been written yet.')) { await act(`/${id}/move/${mid}/cancel`); onDone(); } };

  if (!m) return <div className="p-6 text-[#5f6368]">{err || 'Loading…'}</div>;
  const step = m.status === 'collect' || m.status === 'needs_manager' ? 1 : m.status === 'target' ? 2 : m.status === 'make_space' ? 3 : 4;

  return (
    <div className="min-h-screen bg-[#e5e7eb] text-[#111111]"><div className="p-4 sm:p-6 max-w-3xl">
      {sign.box}
      <button onClick={onDone} className="text-[#6b7280] text-sm hover:text-[#111111]">← Back</button>
      {!canWork && <div className="text-sm text-[#5f6368] mb-2 px-3 py-2 rounded-lg bg-white border border-[#d1d5db] inline-block">This is a P&P station — inbound work happens at an inbound station; here you can look.</div>}
      <h1 className="text-2xl font-bold text-[#111111] mt-1">Move {m.style_key} <span className="text-[#5f6368] font-normal text-base">box {boxLabel} · from {m.from_display}{m.to_display ? ` → ${m.to_display}` : ''}</span></h1>
      <div className="text-sm text-[#5f6368] mt-1">Step {step} of 3 · {['', 'collect the old pieces', 'the new shelf', 'make space', 'done'][step]} <span className="ml-3">{sign.chip}</span></div>
      {err && <div className="text-red-700 text-sm mt-3">{err}</div>}

      {step === 1 && (
        <div className="mt-4 bg-white rounded-xl border border-[#d1d5db] p-4">
          <div className="text-[#111111] font-medium mb-1">1. Take the old pieces off {m.from_display}</div>
          <div className="text-[#6b7280] text-xs mb-3">The WMS expects these pieces on the shelf: the shop's stock plus pieces already sold but not picked yet (take those along too — the picker is sent to the new shelf). Type what you found per size.</div>
          {(m.expected || []).some(e => (e.in_flight || 0) > 0) && (
            <div className="mb-3 bg-amber-100 border border-amber-700 rounded-lg p-2 text-sm text-amber-100">
              A picker is on the way for {(m.expected || []).reduce((n, e) => n + (e.in_flight || 0), 0)} piece(s) of this product right now — their tablet still says {m.from_display}. Leave those pieces, or hand them to the picker.
            </div>
          )}
          <table className="text-sm w-full">
            <tbody>
              {(m.expected || []).map(e => (
                <tr key={e.wms_id} className="border-t border-[#d1d5db]">
                  <td className="py-1.5 text-[#111111] font-semibold w-24">{e.size}</td>
                  <td className="text-[#6b7280] text-xs">{e.article_number}</td>
                  <td className="text-right text-[#374151] w-56">expected <b className="text-[#111111]">{e.expected}</b>{(e.unpicked || 0) > 0 && <span className="text-[#6b7280] text-xs"> = {e.live} stock + {e.unpicked} sold, not picked</span>}</td>
                  <td className="text-right w-24"><input value={found[String(e.wms_id)] ?? ''} onChange={ev => setFound({ ...found, [String(e.wms_id)]: ev.target.value.replace(/\D/g, '') })} className={`${input} w-16 text-center`} /></td>
                </tr>
              ))}
              {(m.expected || []).length === 0 && <tr><td className="py-2 text-[#6b7280]">The WMS has no stock of this product at {m.from_display} — nothing to collect.</td></tr>}
            </tbody>
          </table>
          {m.status === 'needs_manager' ? (
            <div className="mt-3 bg-amber-100 border border-amber-700 rounded-lg p-3 text-sm text-amber-100">
              Not all pieces were found — <b>call a manager</b>. The managers have been told; the move waits here. A manager presses Resume from their own login (not from the station).
              {manager && <button disabled={busy || !canWork} onClick={() => act(`/${id}/move/${mid}/resume`)} className="ml-3 px-3 py-1 bg-amber-700 hover:bg-amber-600 rounded text-white">Resume (manager)</button>}
            </div>
          ) : (
            <div className="mt-3 flex flex-wrap gap-2">
              <button disabled={busy || !canWork} onClick={() => collect(true)} className="px-4 py-2 bg-green-700 hover:bg-green-600 text-white text-sm rounded-lg">Found them all →</button>
              <button disabled={busy || !canWork} onClick={() => collect(false)} className="px-4 py-2 bg-amber-700 hover:bg-amber-600 text-white text-sm rounded-lg">Couldn't find all — call a manager</button>
              <button disabled={busy || !canWork} onClick={cancel} className="px-3 py-2 text-[#5f6368] text-sm underline">cancel the move</button>
            </div>
          )}
        </div>
      )}

      {step === 2 && (
        <div className="mt-4 bg-white rounded-xl border border-[#d1d5db] p-4">
          <div className="text-[#111111] font-medium mb-1">2. The new shelf</div>
          <div className="text-[#6b7280] text-xs mb-3">Type the section you chose, or scan its label.</div>
          <div className="flex flex-wrap items-center gap-2">
            <input autoFocus value={target} onChange={e => setTarget(e.target.value)} placeholder="K 45" onKeyDown={e => { if (e.key === 'Enter') chooseTarget(target); }} className={`${input} w-32 text-lg`} />
            <button disabled={busy || !target.trim()} onClick={() => chooseTarget(target)} className="px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-lg">This shelf →</button>
            <button disabled={busy || !canWork} onClick={cancel} className="px-3 py-2 text-[#5f6368] text-sm underline">cancel the move</button>
          </div>
        </div>
      )}

      {step === 3 && (
        <div className="mt-4 bg-white rounded-xl border border-[#d1d5db] p-4">
          <div className="text-[#111111] font-medium mb-1">3. Make space on {m.to_display}</div>
          <div className="text-[#6b7280] text-xs mb-3">These products live there now. Give each size you move away its new shelf and the pieces you counted; sizes you leave stay.</div>
          {(m.at_target || []).map(g => (
            <div key={g.key} className="mb-3">
              <div className="text-[#1f2937] text-sm font-medium">{g.name || g.master_sku}</div>
              <table className="text-sm w-full">
                <tbody>
                  {g.sizes.map(sz => {
                    const a = away[String(sz.wms_id)] || { to: '', remaining: '' };
                    return (
                      <tr key={sz.wms_id} className="border-t border-[#d1d5db]">
                        <td className="py-1.5 text-[#111111] w-24">{sz.size}</td>
                        <td className="text-[#6b7280] text-xs">{sz.article_number}</td>
                        <td className="text-right text-[#374151] w-24">stock {sz.stock}</td>
                        <td className="w-28 text-right"><input value={a.to} placeholder="to shelf" onChange={e => setAway({ ...away, [String(sz.wms_id)]: { ...a, to: e.target.value } })} className={`${input} w-24`} /></td>
                        <td className="w-24 text-right"><input value={a.remaining} placeholder="pcs" title="pieces counted" onChange={e => setAway({ ...away, [String(sz.wms_id)]: { ...a, remaining: e.target.value.replace(/\D/g, '') } })} className={`${input} w-16 text-center`} /></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ))}
          <div className="mt-2 flex flex-wrap gap-2">
            <button disabled={busy || !canWork} onClick={() => makeSpace(false)} className="px-4 py-2 bg-green-700 hover:bg-green-600 text-white text-sm rounded-lg">Move these away & put {m.style_key.split(' ')[0]} on {m.to_display} ✓</button>
            <button disabled={busy || !canWork} onClick={() => makeSpace(true)} className="px-3 py-2 bg-[#dfe3e8] hover:bg-[#c6cdd5] text-[#111111] text-sm rounded-lg">There is room — nothing to move</button>
            <button disabled={busy || !canWork} onClick={cancel} className="px-3 py-2 text-[#5f6368] text-sm underline">cancel the move</button>
          </div>
        </div>
      )}

      {step === 4 && (
        <div className="mt-4 bg-white rounded-xl border border-[#d1d5db] p-4 text-sm text-[#374151] space-y-2">
          <div>This move is <b className="text-[#111111]">{m.status}</b>{m.count_differs && <span className="text-amber-700"> — the pieces did not add up (Review)</span>}. <button onClick={done} className="underline">Back</button></div>
          {(m.expected || []).length > 0 && (
            <div><div className="text-[#6b7280] text-xs uppercase tracking-wider">Collected at {m.from_display}</div>
              {(m.expected || []).map(e => <div key={e.wms_id}>{e.size}: found {m.found?.[String(e.wms_id)] ?? '—'} of {e.expected} expected</div>)}</div>
          )}
          {((m as any).moved_away || []).length > 0 && (
            <div><div className="text-[#6b7280] text-xs uppercase tracking-wider">Moved away from {m.to_display}</div>
              {((m as any).moved_away || []).map((x: any) => <div key={x.wms_id}>{x.name} {x.size} → {x.to}: {x.remaining ?? '—'} counted, stock {x.stock}{x.differs && <span className="text-amber-700"> ≠</span>}</div>)}</div>
          )}
          {(m as any).resumed_by && <div className="text-[#6b7280] text-xs">resumed by {(m as any).resumed_by}{(m as any).note ? ` — ${(m as any).note}` : ''}</div>}
        </div>
      )}
    </div></div>
  );
}
